<template>
  <div class="exam-intro-container">
    <div class="exam-intro-card">
      <div class="exam-title">{{ examInfo.title || '考试详情' }}</div>
      
      <div class="exam-meta">
        <div class="meta-item">
          <span class="meta-label">考试时长</span>
          <span class="meta-value blue">{{ examInfo.duration || 0 }}</span>
          <span class="meta-unit blue">分钟</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">试卷总分</span>
          <span class="meta-value blue">{{ examInfo.total || 0 }}</span>
          <span class="meta-unit blue">分</span>
        </div>
        <div class="meta-item">
          <span class="meta-label">及格分数</span>
          <span class="meta-value blue">{{ examInfo.pass || 0 }}</span>
          <span class="meta-unit blue">分</span>
        </div>
        <!-- <div class="meta-item">
          <span class="meta-value red">可答题2次</span>
        </div>
        <div class="meta-item">
          <span class="meta-value gray">14人已答题</span>
        </div> -->
      </div>
      
      <!-- <div class="score-rule">
        考试总分= 单选题得分*60% + 多选题得分*60% + 问答题得分*30%
      </div> -->
      
      <div class="intro-section">
        <div class="section-title">题型介绍</div>
        <div class="section-content">
          单项选择题（选项中只有1个正确答案）；多项选择题（选项中至少有2个正确答案）；判断题（选项中只有1个正确答案）。
        </div>
      </div>
      
      <div class="intro-section">
        <div class="section-title">考前必读</div>
        <div class="section-content">
          <div class="rule-item">1.答题者准备好后，可点击“开始做题”按钮开始考试；</div>
          <div class="rule-item">2.做题前可看题目后的标签，明白此题是单选、多选还是判断；</div>
          <div class="rule-item">3.答题卡可查看该题是‘已答’或‘未答’状态；</div>
          <div class="rule-item">4.作答所有的题目点击“提交并查看结果”按钮，即可查看自己的考试结果。</div>
        </div>
      </div>
      
      <div class="action-area">
        <el-button type="primary" class="start-btn" @click="startExam">开始答题</el-button>
        <div class="back-link" @click="goBack">
          返回试题列表 <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </div>

    <el-dialog
      title="实名认证"
      :visible.sync="realNameDialogVisible"
      width="400px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form :model="realNameForm" :rules="realNameRules" ref="realNameForm" label-width="80px">
        <el-form-item label="真实姓名" prop="name">
          <el-input v-model="realNameForm.name" placeholder="请输入真实姓名"></el-input>
        </el-form-item>
        <el-form-item label="身份证号" prop="idCard">
          <el-input v-model="realNameForm.idCard" placeholder="请输入身份证号"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="realNameDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="realNameSubmitting" @click="handleRealNameVerify">提交认证</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createLivenessDetection, getLivenessResult, verifyFaceCompare, verifyRealName } from '@/api/authApi'
import { mapGetters } from 'vuex'
import { getMyExamList, reportFaceFail } from '@/api/examApi'

export default {
  name: 'ExamIntro',
  data() {
    return {
      examId: null,
      examInfo: {},
      verifying: false,
      examLoading: false,
      realNameDialogVisible: false,
      realNameSubmitting: false,
      realNameForm: {
        name: '',
        idCard: ''
      },
      realNameRules: {
        name: [{ required: true, message: '请输入真实姓名', trigger: 'blur' }],
        idCard: [
          { required: true, message: '请输入身份证号', trigger: 'blur' },
          { pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/, message: '身份证号格式不正确', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['isRealNameVerified', 'isLoggedIn']),
    enableFaceRecognition() {
      const raw = this.examInfo && (this.examInfo.enableFaceRecognition ?? this.examInfo.enable_face_recognition)
      if (Number(raw) !== 1) return false
      if (Number(this.examInfo.faceExempt) === 1) return false
      return true
    },
    maxFaceAttempts() {
      const val = this.examInfo && this.examInfo.maxFaceAttempts
      return Number(val) || 0
    },
    faceFailCount() {
      const val = this.examInfo && this.examInfo.faceFailCount
      return Number(val) || 0
    },
    faceLimitReached() {
      if (!this.maxFaceAttempts) return false
      return this.faceFailCount >= this.maxFaceAttempts
    }
  },
  async created() {
    this.examId = this.$route.params.id
    this.examInfo = this.normalizeExamInfoFromRoute(this.$route.query || {})
    await this.loadExamInfoFromMyList()

    // 检查是否有进行中的活体检测
    const pendingOrderNo = sessionStorage.getItem('exam_liveness_order')
    if (pendingOrderNo) {
      await this.handleLivenessCallback(pendingOrderNo)
    }
  },
  methods: {
    normalizeExamInfoFromRoute(query) {
      const q = query || {}
      const title = q.title || q.examName || q.paperName
      const duration = q.duration || q.answerDuration
      const total = q.total || q.totalScore
      const pass = q.pass || q.passScore
      return {
        ...q,
        title: title != null ? String(title) : '',
        duration: duration != null ? Number(duration) : 0,
        total: total != null ? Number(total) : 0,
        pass: pass != null ? Number(pass) : 0
      }
    },
    normalizeExamInfoFromApi(exam) {
      const e = exam || {}
      return {
        ...e,
        title: String(e.examName || e.paperName || e.courseName || ''),
        duration: Number(e.answerDuration || 0),
        total: Number(e.totalScore || 0),
        pass: Number(e.passScore || 0)
      }
    },
    parseDatetime(val) {
      if (val == null || val === '') return null
      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val

      if (typeof val === 'number') {
        const n = Number(val)
        if (!Number.isFinite(n)) return null
        const ms = n < 1e12 ? n * 1000 : n
        const d = new Date(ms)
        return Number.isNaN(d.getTime()) ? null : d
      }

      const s = String(val).trim()
      if (!s) return null
      if (/^\d+$/.test(s)) {
        const n = Number(s)
        if (!Number.isFinite(n)) return null
        const ms = n < 1e12 ? n * 1000 : n
        const d = new Date(ms)
        return Number.isNaN(d.getTime()) ? null : d
      }

      const normalized = s.replace(/-/g, '/')
      const dt = new Date(normalized)
      return Number.isNaN(dt.getTime()) ? null : dt
    },
    getStatusNumByTime(startVal, endVal) {
      const start = this.parseDatetime(startVal)
      const end = this.parseDatetime(endVal)
      if (!start && !end) return null

      const now = Date.now()
      if (start && now < start.getTime()) return 0
      if (end && now > end.getTime()) return 2
      return 1
    },
    getExamWindow() {
      const startVal = this.examInfo && (this.examInfo.examStartTime || this.examInfo.exam_start_time)
      const endVal = this.examInfo && (this.examInfo.examEndTime || this.examInfo.exam_end_time)
      const startTime = this.parseDatetime(startVal)
      const endTime = this.parseDatetime(endVal)
      const statusNum = this.getStatusNumByTime(startVal, endVal)
      return { startTime, endTime, statusNum }
    },
    async loadExamInfoFromMyList() {
      if (!this.isLoggedIn) return
      if (!this.examId) return
      if (this.examLoading) return
      this.examLoading = true
      try {
        const pageSize = 50
        let pageNum = 1
        let found = null

        while (pageNum <= 20 && !found) {
          const res = await getMyExamList({ pageNum, pageSize })
          const data = res && res.data ? res.data : {}
          const list = Array.isArray(data.list) ? data.list : []
          found = list.find(it => String(it && it.id) === String(this.examId)) || null
          const total = Number(data.total || 0)
          if (!total) break
          if (pageNum * pageSize >= total) break
          pageNum += 1
        }

        if (found) {
          this.examInfo = this.normalizeExamInfoFromApi(found)
        }
      } catch (e) {
        void e
      } finally {
        this.examLoading = false
      }
    },
    openRealNameVerify() {
      this.realNameDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.realNameForm) this.$refs.realNameForm.resetFields()
      })
    },
    handleRealNameVerify() {
      if (!this.$refs.realNameForm) return
      this.$refs.realNameForm.validate(async valid => {
        if (!valid) return
        this.realNameSubmitting = true
        try {
          const res = await verifyRealName(this.realNameForm)
          const data = (res && res.data) || {}
          if (!data.passed) {
            this.$message.error(data.reason || '认证失败')
            return
          }
          this.$message.success('实名认证成功')
          this.realNameDialogVisible = false
          if (this.$store) {
            const current = (this.$store.state && this.$store.state.auth && this.$store.state.auth.userInfo) || {}
            this.$store.dispatch('auth/updateUserInfo', {
              ...current,
              isRealNameVerified: true,
              realName: String(this.realNameForm.name || '').trim()
            })
          }
          await this.startLivenessDetection()
        } catch (err) {
          this.$message.error((err && err.message) || '认证请求失败')
        } finally {
          this.realNameSubmitting = false
        }
      })
    },
    async handleLivenessCallback(orderNo) {
      this.verifying = true
      const loading = this.$loading({
        lock: true,
        text: '正在验证身份信息...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      
      try {
        // 1. 查询活体检测结果
        const livenessRes = await getLivenessResult({ orderNo })
        const livenessData = livenessRes.data || {}
        
        if (!livenessData.passed) {
          throw new Error(livenessData.reason || '活体检测未通过')
        }

        // 2. 人脸比对
        const compareRes = await verifyFaceCompare({ faceUrl: livenessData.faceImageUrl })
        const compareData = compareRes.data || {}
        
        if (!compareData.passed) {
          throw new Error(compareData.reason || '人脸比对未通过')
        }

        // 验证成功，清除缓存并进入考试
        sessionStorage.removeItem('exam_liveness_order')
        sessionStorage.setItem(`exam_face_passed_${this.examId}`, String(Date.now()))
        this.$message.success('身份验证通过，正在进入考试...')
        const query = {}
        query.mode = (this.$route && this.$route.query && this.$route.query.mode) || 'exam'
        const returnPath = this.$route && this.$route.query && this.$route.query.returnPath
        if (returnPath) query.returnPath = returnPath
        this.$router.replace({ path: `/exam-detail/${this.examId}`, query })
        
      } catch (err) {
        sessionStorage.removeItem('exam_liveness_order')
        const reason = (err && err.message) || '身份验证失败'
        try { await reportFaceFail(this.examId, reason) } catch (e) { void e }
        const newFailCount = this.faceFailCount + 1
        if (this.maxFaceAttempts && newFailCount >= this.maxFaceAttempts) {
          this.$alert(`人脸识别失败${newFailCount}次，已达到上限（${this.maxFaceAttempts}次），无法进入考试，请联系教师开启免识别。`, '人脸识别次数已达上限', {
            confirmButtonText: '确定',
            type: 'error'
          })
        } else {
          const remain = this.maxFaceAttempts ? Math.max(0, this.maxFaceAttempts - newFailCount) : ''
          const remainText = remain !== '' ? `，还剩${remain}次机会` : ''
          this.$alert(`${reason}${remainText}`, '验证失败', {
            confirmButtonText: '确定',
            type: 'error'
          })
        }
      } finally {
        loading.close()
        this.verifying = false
      }
    },
    async startExam() {
      if (!this.examId) return

      const remainTimes = Number(this.examInfo && this.examInfo.remainTimes)
      const allowTimes = Number(this.examInfo && this.examInfo.allowTimes)
      if (Number.isFinite(allowTimes) && allowTimes > 0 && Number.isFinite(remainTimes) && remainTimes <= 0) {
        this.$message.warning('考试次数已用尽')
        return
      }

      const { startTime, statusNum } = this.getExamWindow()
      const now = new Date()
      if (statusNum === 0) {
        this.$message.warning('考试尚未开始')
        return
      }
      if (statusNum === 2) {
        this.$message.warning('考试已结束')
        return
      }

      const status = Number(this.examInfo && this.examInfo.status)
      if (statusNum == null && Number.isFinite(status) && status !== 1) {
        const statusName = String((this.examInfo && (this.examInfo.statusName || this.examInfo.status_name)) || '')
        this.$message.warning(statusName || '当前不在可考试时间段')
        return
      }

      const forbidEnterMinutes = Number(this.examInfo && this.examInfo.forbidEnterMinutes)
      if (startTime && Number.isFinite(forbidEnterMinutes) && forbidEnterMinutes > 0) {
        const deadline = new Date(startTime.getTime() + forbidEnterMinutes * 60 * 1000)
        if (now > deadline) {
          this.$message.warning('已超过允许进入考试时间')
          return
        }
      }

      if (!this.enableFaceRecognition) {
        const query = {}
        query.mode = (this.$route && this.$route.query && this.$route.query.mode) || 'exam'
        const returnPath = this.$route && this.$route.query && this.$route.query.returnPath
        if (returnPath) query.returnPath = returnPath
        this.$router.replace({ path: `/exam-detail/${this.examId}`, query })
        return
      }

      if (this.faceLimitReached) {
        this.$alert(`人脸识别失败${this.faceFailCount}次，已达到上限（${this.maxFaceAttempts}次），无法进入考试，请联系教师开启免识别。`, '人脸识别次数已达上限', {
          confirmButtonText: '确定',
          type: 'error'
        })
        return
      }

      // 1. 检查实名认证状态
      if (!this.isRealNameVerified) {
        this.openRealNameVerify()
        return
      }

      await this.startLivenessDetection()
    },
    async startLivenessDetection() {
      if (!this.examId) return
      try {
        const res = await createLivenessDetection({
          actionLiveList: "LookLeft,BlinkEye",
          actionMutex: false,
          antiCameraHack: false,
          backLiveOn: false,
          foreLiveOn: true,
          hideGuidePage: false,
          returnUrl: window.location.href,
          showFail: false,
          showSuccess: false,
          style: 1,
          title: "考试人脸检测",
          wxMiniProgram: false
        })

        const data = res.data || {}
        if (data.url && data.orderNo) {
          // 保存订单号用于回调验证
          sessionStorage.setItem('exam_liveness_order', data.orderNo)
          // 跳转到H5检测页面
          window.location.href = data.url
        } else {
          this.$message.error('无法启动人脸检测，请稍后重试')
        }
      } catch (err) {
        this.$message.error(err.message || '启动人脸检测失败')
      }
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped lang="scss">
.exam-intro-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f7fa;
  padding: 40px 0;
}

.exam-intro-card {
  width: 1000px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 60px 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.exam-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 40px;
}

.exam-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
}

.meta-item {
  display: flex;
  align-items: baseline;
}

.meta-label {
  font-size: 16px;
  color: #333;
  margin-right: 10px;
}

.meta-value {
  font-size: 16px;
  
  &.blue {
    font-size: 40px;
    color: #3E2CCD;
    font-weight: 500;
    margin-right: 5px;
  }
  
  &.red {
    color: #FF4433;
  }
  
  &.gray {
    color: #999;
  }
}

.meta-unit {
  font-size: 16px;
  color: #3E2CCD;
}

.score-rule {
  font-size: 16px;
  color: #FF4433;
  margin-bottom: 60px;
}

.intro-section {
  width: 100%;
  margin-bottom: 40px;
  
  .section-title {
    font-size: 20px;
    font-weight: 600;
    color: #3E2CCD;
    margin-bottom: 20px;
  }
  
  .section-content {
    font-size: 14px;
    color: #666;
    line-height: 2;
  }
}

.action-area {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.start-btn {
  width: 100%;
  height: 50px;
  font-size: 18px;
  background-color: #3E2CCD;
  border-color: #3E2CCD;
  border-radius: 6px;
  margin-bottom: 20px;
  
  &:hover {
    background-color: #3325ad;
    border-color: #3325ad;
  }
}

.back-link {
  font-size: 16px;
  color: #3E2CCD;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
  
  i {
    margin-left: 5px;
  }
}
</style>
